import CloseIcon from '@mui/icons-material/Close';
import {
  DialogActions,
  DialogTitle,
  IconButton,
  Theme,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import React from 'react';
import { IModalWithStyles, ModalWithStyles } from './ModalWithStyles';

const useStyles = makeStyles((theme: Theme) => ({
  dialogTitle: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: '48px',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  modal: {
    '& .MuiDialogContent-root': {
      padding: '0px 13px',
    },
  },
  modalMain: {
    padding: 0,
  },
  dialogActions: {
    padding: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  scrollWithin: {
    '&::-webkit-scrollbar': {
      '-webkit-appearance': 'none',
      width: 7,
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 4,
      backgroundColor: 'rgba(0, 0, 0, .5)',
      boxShadow: '0 0 1px rgba(255, 255, 255, .5)',
    },
    maxHeight: 'calc(100vh - 210px)',
    overflow: 'auto',
  },
  navigation: {
    padding: 0,
    backgroundColor: theme.palette.divider,
  },
}));

interface IWizardModal extends IModalWithStyles {
  /** Component containing tab links */
  navigation: React.ReactNode;
  /** Component containing action buttons */
  dialogActions: React.ReactNode;
  hideTabs?: boolean;
}
/**
 * Modal for wizard interface
 */
export function WizardModal({
  navigation,
  dialogActions,
  className,
  title,
  show,
  children,
  onHide,
  onSubmit,
  disableSubmit,
  isLoading = false,
  isSubmitting = false,
  size = 'md',
  customWidth = 'none',
  hideTabs = false,
}: IWizardModal) {
  const classes = useStyles();

  return (
    <ModalWithStyles
      customWidth={customWidth}
      className={clsx(classes.modal, className)}
      title={title}
      show={show}
      onHide={onHide}
      onSubmit={onSubmit}
      disableSubmit={disableSubmit}
      isLoading={isLoading}
      isSubmitting={isSubmitting}
      size={size}
      hideHeader={true}
      hideFooter={true}
    >
      <div className="row">
        {hideTabs ? null : (
          <nav className={clsx('col-lg-3 modalTabs', classes.navigation)}>
            {navigation}
          </nav>
        )}
        <aside
          className={clsx(
            hideTabs ? 'col-lg-12' : 'col-lg-9',
            classes.modalMain
          )}
        >
          <DialogTitle className={classes.dialogTitle}>
            <Typography>{title}</Typography>
            {onHide ? (
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={onHide}
                size="large"
              >
                <CloseIcon />
              </IconButton>
            ) : null}
          </DialogTitle>
          <main className={classes.scrollWithin}>{children}</main>
          <DialogActions className={classes.dialogActions}>
            {dialogActions}
          </DialogActions>
        </aside>
      </div>
    </ModalWithStyles>
  );
}
